const MOYASAR_CSS_URL =
    "https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.7/dist/moyasar.css";
const MOYASAR_JS_URL =
    "https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.7/dist/moyasar.umd.min.js";

let moyasarAssetsPromise;

function normalizeAbsoluteUrl(pathname) {
    if (!pathname) return "";
    if (/^https?:\/\//i.test(pathname)) return pathname;
    const normalizedPath = String(pathname).startsWith("/")
        ? String(pathname)
        : `/${String(pathname)}`;
    return `${window.location.origin}${normalizedPath}`;
}

function loadStylesheetOnce(href) {
    const existing = document.querySelector(`link[href="${href}"]`);
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () =>
            reject(new Error("Failed to load Moyasar stylesheet"));
        document.head.appendChild(link);
    });
}

function loadScriptOnce(src) {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && window.Moyasar) {
        return Promise.resolve();
    }
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener(
                "error",
                () => reject(new Error("Failed to load Moyasar script")),
                { once: true }
            );
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () =>
            reject(new Error("Failed to load Moyasar script"));
        document.body.appendChild(script);
    });
}

export function ensureAssetsLoaded() {
    if (!moyasarAssetsPromise) {
        moyasarAssetsPromise = Promise.all([
            loadStylesheetOnce(MOYASAR_CSS_URL),
            loadScriptOnce(MOYASAR_JS_URL),
        ]).then(() => {
            if (!window.Moyasar || typeof window.Moyasar.init !== "function") {
                throw new Error("Moyasar library not available");
            }
        });
    }
    return moyasarAssetsPromise;
}

export function resolvePublishableKey(config = {}) {
    const gatewayConfig = config.moyasar || {};
    return (
        gatewayConfig.publishable_api_key ||
        gatewayConfig.publishable_key ||
        gatewayConfig.public_key ||
        gatewayConfig.publishableKey ||
        ""
    );
}

export function resolveSupportedNetworks(config = {}) {
    const value =
        config?.moyasar?.supported_networks ||
        config?.moyasar?.supportedNetworks;
    if (Array.isArray(value) && value.length) {
        return value;
    }
    if (typeof value === "string" && value.trim()) {
        return value
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return ["visa", "mastercard", "mada"];
}

export function resolveLanguage() {
    const lang = String(
        document.documentElement.lang ||
            navigator.language ||
            navigator.userLanguage ||
            "en"
    )
        .trim()
        .toLowerCase()
        .slice(0, 2);
    return ["en", "ar"].includes(lang) ? lang : "en";
}

export function resolveCallbackUrl(internalId) {
    return normalizeAbsoluteUrl(
        `/moyasar_redirect/${encodeURIComponent(String(internalId || ""))}`
    );
}

export function getInitParamsFromOrder(res) {
    const nextAction = res?.data?.client_next_action || {};
    const amount = Number(nextAction.transaction_minor_amount);
    const currency = String(nextAction.transaction_currency || "").toUpperCase();
    const givenId = nextAction.transaction_id;

    if (!Number.isFinite(amount) || amount <= 0 || !currency) {
        throw new Error("Missing Moyasar payment initialization fields");
    }

    return {
        amount,
        currency,
        givenId,
    };
}

export function buildApplePayConfig(config = {}) {
    return {
        country: String(
            config?.moyasar?.apple_pay_country || config.countryCode || "SA"
        ).toUpperCase(),
        label: String(config.brandName || "HeyCharge"),
        validate_merchant_url: "https://api.moyasar.com/v1/applepay/initiate",
    };
}

export async function setRedirectContext(flow, context) {
    const store = (await import("@/store")).default;
    await store.dispatch("redirect/setRedirect", { flow, context });
}

export async function prepareMoyasarPayment({
    selectedMethod,
    selectedSubscription,
    deviceCode,
    outletId,
    config,
    authorizationSubscriptionId,
    createPaymentOrder,
    doSubscriptionPayment,
}) {
    const gatewayId = config?.moyasarGatewayId || 0;
    const publishableKey = resolvePublishableKey(config);

    if (!gatewayId) {
        throw new Error("Missing Moyasar gateway configuration");
    }
    if (!publishableKey) {
        throw new Error("Missing Moyasar publishable_api_key");
    }

    let res;
    let redirectFlow;
    let redirectContext;

    if (selectedSubscription?.id) {
        const autoRenew = Number(selectedSubscription.auto_renew ?? 0);
        res = await doSubscriptionPayment({
            subscriptionId: selectedSubscription.id,
            paymentMethod: selectedMethod,
            gatewayId,
            outletsId: outletId,
            autoRenew,
        });

        const internalId = res?.data?.client_next_action?.internal_id;
        if (!internalId) {
            throw new Error("Missing internal_id");
        }

        redirectFlow = "moyasarSubscriptionConfirm";
        redirectContext = {
            internal_id: internalId,
            auto_renew: autoRenew,
        };
    } else {
        res = await createPaymentOrder(selectedMethod, gatewayId, deviceCode);

        const internalId = res?.data?.client_next_action?.internal_id;
        const rentPaymentId = res?.data?.rent_payment_id;
        if (!internalId) {
            throw new Error("Missing internal_id");
        }
        if (!rentPaymentId) {
            throw new Error("Missing rent_payment_id");
        }

        redirectFlow = "moyasarSaveAndBorrow";
        redirectContext = {
            internal_id: internalId,
            rent_payment_id: rentPaymentId,
            subscription_id: authorizationSubscriptionId,
        };
    }

    await setRedirectContext(redirectFlow, redirectContext);

    const { amount, currency, givenId } = getInitParamsFromOrder(res);
    const internalId = redirectContext.internal_id;

    return {
        amount,
        currency,
        givenId,
        internalId,
        publishableKey,
    };
}

export function initMoyasarForm({
    element,
    selectedMethod,
    amount,
    currency,
    internalId,
    givenId,
    publishableKey,
    config,
}) {
    if (!element) {
        throw new Error("Missing Moyasar mount element");
    }
    if (!publishableKey) {
        throw new Error("Missing Moyasar publishable_api_key");
    }
    if (!window.Moyasar || typeof window.Moyasar.init !== "function") {
        throw new Error("Moyasar library not available");
    }

    element.innerHTML = "";

    const initOptions = {
        element,
        amount,
        currency,
        description: String(internalId || ""),
        publishable_api_key: publishableKey,
        callback_url: resolveCallbackUrl(internalId),
        supported_networks: resolveSupportedNetworks(config),
        language: resolveLanguage(),
        methods: [selectedMethod],
    };

    if (givenId) {
        initOptions.given_id = String(givenId);
    }
    if (selectedMethod === "applepay") {
        initOptions.apple_pay = buildApplePayConfig(config);
    }

    window.Moyasar.init(initOptions);
}
